/**
 * Shared hook that encapsulates all widget page data fetching, URL parameter
 * resolution, metric tracking, and card config generation.
 *
 * Both WidgetPage (postMessage) and WidgetPagePortal (same-origin portal)
 * consume this hook so the business logic lives in one place.
 */
import { useMemo, useEffect } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import ClientLogger from 'src/utils/logger';
import { ClientLog } from 'src/constants/clientLog';
import { useLearningModule } from 'src/hooks';
import { LearningModuleStateData } from 'src/types/state/learningModule';
import { timeConverter } from 'src/utils/timeFormat';
import { useBundle } from '@amzn/react-arb-tools';
import { BUNDLE_NAME, LEARNING_PATHWAY_STRING_ID } from 'src/constants/localization';
import { t } from 'src/utils/translation';
import { EntityType } from 'src/constants/entity';
import { FeatureName } from 'src/metrics/metricsFields';
import { useMetrics } from 'src/metrics/useUserMetrics';
import usePagePerformance from 'src/metrics/usePagePerformance';
import { getModuleDownloadRefTag } from 'src/constants/refTag';
import { extractRefTag, extractReferral } from 'src/utils/refParameterConversion';
import { DEFAULT_VIDEO_SRC_PREFERENCE } from 'src/constants/video';
import { WidgetCardConfig, WidgetDisplayMode } from './types';

export interface UseWidgetPageDataProps {
  widgetCardConfig?: WidgetCardConfig;
  parentType?: EntityType;
  parentId?: string;
  unauthenticated?: boolean;
  clientId?: string;
}

/**
 * Resolves the final value for `unauthenticated` from props and URL params.
 */
const resolveUnauthenticated = (
  prop: boolean | undefined,
  urlParams: URLSearchParams,
): boolean | undefined => {
  if (prop !== undefined) return prop;
  if (urlParams.has('unauthenticated')) return urlParams.get('unauthenticated') === 'true';
  return undefined;
};

const mapInstanceTypeToContentVariant = (instanceType: string): 'Video' | 'Pdf' => {
  switch (instanceType.toUpperCase()) {
    case 'VIDEO':
      return 'Video';
    case 'DOCUMENT':
    default:
      return 'Pdf';
  }
};

const getDefaultInstance = (module: LearningModuleStateData) => {
  if (!module.instanceMap || !module.defaultLanguage) return null;
  return module.instanceMap[module.defaultLanguage];
};

const resolveParentType = (
  prop: EntityType | undefined | null,
  urlParams: URLSearchParams,
): EntityType => {
  if (prop !== undefined && prop !== null) return prop;
  const urlValue = urlParams.get('parentType');
  if (urlValue && Object.values(EntityType).includes(urlValue as EntityType)) {
    return urlValue as EntityType;
  }
  return EntityType.ModulePage;
};

const VALID_DISPLAY_MODES: WidgetDisplayMode[] = ['card', 'inline'];

const resolveDisplay = (
  prop: WidgetDisplayMode | undefined,
  urlParams: URLSearchParams,
): WidgetDisplayMode => {
  if (prop && VALID_DISPLAY_MODES.includes(prop)) return prop;
  const urlValue = urlParams.get('display') as WidgetDisplayMode | null;
  if (urlValue && VALID_DISPLAY_MODES.includes(urlValue)) return urlValue;
  return 'card';
};

/**
 * Resolves the best available content URL from a module's default instance.
 * For VIDEO instances: follows MEDIA_CENTRAL → YOUTUBE preference.
 * For DOCUMENT instances: returns the PDF URL.
 */
const resolveContentUrl = (module: LearningModuleStateData): string | null => {
  const instance = getDefaultInstance(module);
  if (!instance || !instance.instanceType) return null;

  const type = instance.instanceType.toUpperCase();
  if (type === 'VIDEO') {
    const { urlMap } = instance;
    if (!urlMap) return null;
    const matchedKey = DEFAULT_VIDEO_SRC_PREFERENCE.find((key) => urlMap[key]);
    return matchedKey ? urlMap[matchedKey]! : null;
  }
  if (type === 'DOCUMENT') {
    return instance.urlMap?.PDF ?? null;
  }
  return null;
};

const useWidgetPageData = ({
  widgetCardConfig,
  parentType,
  parentId,
  unauthenticated,
  clientId,
}: UseWidgetPageDataProps) => {
  const { learningModuleId } = useParams();
  const location = useLocation();

  // URL query string parameters (iframe usage)
  const urlParams = new URLSearchParams(location.search);
  const finalClientId = clientId ?? urlParams.get('clientId') ?? undefined;
  const finalUnauthenticated = resolveUnauthenticated(unauthenticated, urlParams);
  const finalParentId = parentId ?? urlParams.get('parentId') ?? learningModuleId;
  const finalParentType = resolveParentType(parentType, urlParams);
  const finalDisplay = resolveDisplay(widgetCardConfig?.display, urlParams);
  const refTag = extractRefTag(urlParams);
  const referral = extractReferral(urlParams);

  const { viewMetric, clickMetric } = useMetrics();

  // Call all hooks unconditionally before validation (Rules of Hooks)
  const {
    learningModule: moduleData,
    isLoading,
    isError,
    error,
  } = useLearningModule(learningModuleId || '');

  const [learningPathwayBundle] = useBundle(BUNDLE_NAME.LEARNING_PATHWAY);

  // Track per-page performance (latency + availability) once the module finishes loading.
  // Called before the learningModuleId validation return to keep hook order stable. A missing
  // learningModuleId skips the RTK query (shouldFetch=false), so isError stays false; we OR in
  // !learningModuleId so the missing-id failure is correctly classified as a Fault rather than a
  // Success (availability = Success/(Success+Fault); mislabeling failures would inflate the metric).
  // learningModuleId is the resource key so navigation between modules (which reuses this hook's
  // component instance) resets the measurement and re-emits metrics for each module.
  usePagePerformance(
    FeatureName.LearningModuleCardWidget,
    isLoading,
    isError || !learningModuleId,
    learningModuleId,
  );

  // Validate learningModuleId after all hooks are called
  if (!learningModuleId) {
    const errorMessage = `Expected learningModuleId to be present in browser URL path: ${location.pathname}`;
    ClientLogger.warn(ClientLog.IllegalInternalDataState, new Error(errorMessage), {
      path: location.pathname,
      url: window.location.href,
    });
    return {
      learningModuleId: undefined,
      location,
      moduleData: null,
      isLoading: false,
      isError: true,
      error: new Error(errorMessage),
      finalCardConfig: null,
      finalDisplay,
      contentUrl: null,
      defaultInstance: null,
      finalClientId,
      finalUnauthenticated,
      finalParentId,
      finalParentType,
      refTag,
      referral,
      emitClickMetric: () => {},
    };
  }

  /** Maps contentVariant to the user-visible display label for metadata badges. */
  const contentVariantDisplayLabel: Record<string, string> = { Video: 'Video', Pdf: 'PDF' };

  /**
   * Builds the metadata array for the widget card.
   * Precondition: module has a valid defaultInstance with instanceType
   * (enforced by finalCardConfig before calling this function).
   * Defensive null check retained for edge cases where instanceMap
   * may become unavailable between finalCardConfig validation and
   * this call (e.g., reactive data updates).
   */
  const getModuleMetadata = (
    module: LearningModuleStateData,
    contentVariant: string,
  ): string[] => {
    const defaultInstance = getDefaultInstance(module);
    if (!defaultInstance || !defaultInstance.instanceType) return [];

    const metadata: string[] = [];
    const label = contentVariantDisplayLabel[contentVariant];
    // label is always defined: contentVariant is 'Video' or 'Pdf' from
    // mapInstanceTypeToContentVariant, both present in the display label map.
    metadata.push(label);

    const instanceType = defaultInstance.instanceType.toUpperCase();
    if (instanceType === 'VIDEO' && defaultInstance.duration) {
      metadata.push(timeConverter(defaultInstance.duration));
    } else if (instanceType === 'DOCUMENT' && defaultInstance.pageSize) {
      metadata.push(t(
        learningPathwayBundle,
        LEARNING_PATHWAY_STRING_ID.LEARNING_PATHWAY_PDF_PAGES,
        { count: defaultInstance.pageSize },
      ));
    }

    return metadata;
  };

  const finalCardConfig = useMemo(() => {
    if (isLoading || isError || !moduleData) return null;

    const defaultInstance = getDefaultInstance(moduleData);
    if (!defaultInstance || !defaultInstance.instanceType) return null;

    const contentVariant = mapInstanceTypeToContentVariant(defaultInstance.instanceType);
    const metadata = getModuleMetadata(moduleData, contentVariant);

    return {
      id: `widget-card-${learningModuleId}`,
      title: moduleData.title,
      description: moduleData.description,
      imageSrc: moduleData.thumbnailLink,
      contentVariant,
      metadata,
      className: 'w-full h-full',
      ...(widgetCardConfig ?? {}),
    };
  }, [moduleData, isLoading, isError, learningModuleId, learningPathwayBundle, widgetCardConfig]);

  // Page view tracking
  useEffect(() => {
    if (!isLoading && !isError && moduleData) {
      const downloadRefTag = getModuleDownloadRefTag(finalParentType);
      viewMetric({
        feature: FeatureName.LearningModuleCardWidget,
        metadata: {
          moduleId: moduleData.moduleId,
          moduleTitle: moduleData.title,
          refTag: downloadRefTag,
          url: window.location.href,
          parentType: finalParentType,
          parentId: finalParentId,
          ...(finalClientId && { clientId: finalClientId }),
        },
      });

      // Widget integration view tracking
      ClientLogger.info(ClientLog.WidgetIntegrationView, {
        learningModuleId: moduleData.moduleId,
        refTag,
        referral,
        path: location.pathname,
        url: window.location.href,
        parentType: finalParentType,
        parentId: finalParentId,
        clientId: finalClientId,
      });
    }
  }, [viewMetric, moduleData, isLoading, isError, finalParentType, finalParentId, finalClientId, refTag, referral]);

  const emitClickMetric = () => {
    if (!moduleData) return;
    const downloadRefTag = getModuleDownloadRefTag(finalParentType);
    clickMetric({
      feature: FeatureName.LearningModuleCardWidget,
      metadata: {
        moduleId: moduleData.moduleId,
        moduleTitle: moduleData.title,
        refTag: downloadRefTag,
        url: window.location.href,
        parentType: finalParentType,
        parentId: finalParentId,
        ...(finalClientId && { clientId: finalClientId }),
      },
    });

    // Widget integration click tracking
    ClientLogger.info(ClientLog.WidgetIntegrationClick, {
      learningModuleId: moduleData.moduleId,
      refTag,
      referral,
      path: location.pathname,
      url: window.location.href,
      parentType: finalParentType,
      parentId: finalParentId,
      clientId: finalClientId,
    });
  };

  const contentUrl = moduleData ? resolveContentUrl(moduleData) : null;
  const defaultInstance = moduleData ? getDefaultInstance(moduleData) : null;

  return {
    learningModuleId,
    location,
    moduleData,
    isLoading,
    isError,
    error,
    finalCardConfig,
    finalDisplay,
    contentUrl,
    defaultInstance,
    finalClientId,
    finalUnauthenticated,
    finalParentId,
    finalParentType,
    refTag,
    referral,
    emitClickMetric,
  };
};

export default useWidgetPageData;
